// src/modules/employees/EmployeeForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhoneAlt, FaLock, FaArrowLeft, FaSave, FaSpinner, FaHardHat, FaDollarSign } from 'react-icons/fa';
import { getEmployee, createEmployee, updateEmployee } from './employeeService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import MaskedInput from '../../components/shared/MaskedInput';
import {
  FormContainer, FormSection, FormHeader, BackButton, FormGrid, FormField, SubmitButton,
} from '../../components/styled/FormComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    username: '', email: '', phoneNumber: '', wage_rate: '', password: '', confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchEmployee = async () => {
      try {
        setInitialLoading(true);
        const data = await getEmployee(id);
        setFormData((s) => ({
          ...s,
          username: data.username || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          wage_rate: data.wage_rate != null ? String(data.wage_rate) : '',
          password: '', confirmPassword: '',
        }));
      } catch (error) {
        setFeedbackMessage('Failed to load employee data. Please try again.');
        setFeedbackSuccess(false);
        setShowFeedback(true);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchEmployee();
  }, [id, isEditMode]);

  const validateForm = () => {
    const e = {};
    if (!formData.username.trim()) e.username = 'Username is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) e.email = 'Invalid email address';
    if (formData.wage_rate !== '' && (isNaN(parseFloat(formData.wage_rate)) || parseFloat(formData.wage_rate) < 0)) {
      e.wage_rate = 'Enter a valid pay rate (0 or more)';
    }
    if (!isEditMode) {
      if (!formData.password) e.password = 'Password is required';
      else if (formData.password.length < 6) e.password = 'Password must be at least 6 characters';
      if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validateForm();
    if (Object.keys(v).length) { setErrors(v); return; }
    setLoading(true);
    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        // '' clears the rate server-side (parseWageRate treats '' as null).
        wage_rate: formData.wage_rate === '' ? null : parseFloat(formData.wage_rate),
      };
      if (formData.password) payload.password = formData.password;
      if (isEditMode) {
        await updateEmployee(id, payload);
        setFeedbackMessage('Employee updated successfully');
      } else {
        await createEmployee(payload);
        setFeedbackMessage('Employee created successfully');
      }
      setFeedbackSuccess(true);
      setShowFeedback(true);
    } catch (error) {
      setFeedbackMessage(error.response?.data?.message || 'Failed to save employee. Please try again.');
      setFeedbackSuccess(false);
      setShowFeedback(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
    if (feedbackSuccess) navigate('/employees');
  };

  if (initialLoading) {
    return <DashboardLayout><LoadingSpinner /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/employees')}>
            <FaArrowLeft /> Back to Employees
          </BackButton>
          <h2>{isEditMode ? 'Edit Employee' : 'Add Employee'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3><FaHardHat style={{ marginRight: 8 }} />Field Employee</h3>
            <FormGrid>
              <FormField index={0}>
                <Input label="Username" icon={<FaUser />} name="username"
                  value={formData.username} onChange={handleChange}
                  placeholder="Enter username" error={errors.username} required />
              </FormField>
              <FormField index={1}>
                <Input label="Email" icon={<FaEnvelope />} name="email" type="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="Enter email address" error={errors.email} required />
              </FormField>
              <FormField index={2}>
                <MaskedInput label="Phone Number" icon={<FaPhoneAlt />} name="phoneNumber"
                  mask="(999) 999-9999" value={formData.phoneNumber} onChange={handleChange}
                  placeholder="Enter phone number" />
              </FormField>
              <FormField index={3}>
                <Input label="Pay Rate ($/hour)" icon={<FaDollarSign />} name="wage_rate" type="number"
                  min="0" step="0.01" value={formData.wage_rate} onChange={handleChange}
                  placeholder="e.g. 22.50" error={errors.wage_rate} />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>{isEditMode ? 'Change Password (Optional)' : 'Password'}</h3>
            <FormGrid>
              <FormField index={4}>
                <Input label="Password" icon={<FaLock />} name="password" type="password"
                  value={formData.password} onChange={handleChange}
                  placeholder={isEditMode ? 'Leave blank to keep current password' : 'Enter password'}
                  error={errors.password} required={!isEditMode} />
              </FormField>
              <FormField index={5}>
                <Input label="Confirm Password" icon={<FaLock />} name="confirmPassword" type="password"
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="Confirm password" error={errors.confirmPassword} required={!isEditMode} />
              </FormField>
            </FormGrid>
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (<><FaSpinner className="icon-spin" />{isEditMode ? 'Updating...' : 'Creating...'}</>)
              : (<><FaSave />{isEditMode ? 'Update Employee' : 'Create Employee'}</>)}
          </SubmitButton>
        </form>
      </FormContainer>

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage}
        success={feedbackSuccess} onComplete={handleFeedbackComplete} />
    </DashboardLayout>
  );
};

export default EmployeeForm;
